import CalendarIcon from 'src/assets/Icons/calendar.svg'
import MarkerIcon from 'src/assets/Icons//marker.svg'
import React, { FunctionComponent } from 'react'
import { InjectedIntlProps, injectIntl } from 'react-intl'
import { Price } from 'src/components/calendar/BuyTickets/Price'
import { TicketButton } from 'src/components/calendar/BuyTickets/TicketButton'
import { LoadingContent } from 'src/components/loading-content/LoadingContent'
import { LoadingPlaceholder } from 'src/components/loading-content/LoadingPlaceholder'
import { Share } from 'src/components/share'
import { guessZone } from 'src/lib/display-date'
import { formattedDateTimeRange, formattedDateStartTime } from 'src/lib/formatter'
import { FanReminderInfo } from './FanReminderInfo'
import { DATE_DISPLAY_OPTIONS } from '../constants'
import locale from '../locale'
const style = require('./TicketWidget.css')

interface Props {
  eventData: any,
  baseUrl: string,
  expanded: boolean,
  loading: boolean,
  listingStatus: string,
  ticketButtonText: string,
  isTimedEntry: boolean,
  viewer: any,
}

const Placeholder = () => (
  <LoadingPlaceholder modifierClass={style.placeholder}>
    <LoadingPlaceholder modifierClass={style.price}>
      <LoadingContent width="40px" height="18px" borderRadius="rounder" />
      <LoadingContent width="112px" height="18px" borderRadius="rounder" />
    </LoadingPlaceholder>

    <LoadingPlaceholder modifierClass={style.content}>
      <LoadingContent height="33px" borderRadius="sharper" />
    </LoadingPlaceholder>
  </LoadingPlaceholder>
)

const TicketWidgetComponent: FunctionComponent<Props & InjectedIntlProps> = ({
  eventData,
  baseUrl,
  expanded,
  loading,
  listingStatus,
  ticketButtonText,
  isTimedEntry,
  intl,
  viewer,
}) => {
  const getEventTime = () => {
    let startAt: Date,
      endAt: Date,
      tz = null

    if (eventData.virtual) {
      startAt = new Date(1000 * eventData.firstTimeSlot.startStamp)
      endAt = new Date(1000 * eventData.firstTimeSlot.endStamp)
      tz = guessZone(startAt)
    } else {
      startAt = eventData.firstTimeSlot.startAt
      endAt = eventData.firstTimeSlot.endAt
    }

    if (eventData.timedEntry) {
      return intl.formatMessage(locale.seeDates)
    }

    // display date and start time (no end time) e.g. Sat, Oct 12 at 6:00 PM
    if (eventData?.dateDisplayOption === DATE_DISPLAY_OPTIONS.startTimeOnly) {
      return formattedDateStartTime({
        startAt,
        intl,
        tz,
      })
    }

    return formattedDateTimeRange({
      startAt,
      endAt,
      intl,
      totalCount: eventData.totalTimeSlotCount,
      tz,
    })
  }

  if (loading) return <Placeholder />
  // TODO: returning null if there are no timeslots? Make state for "noAvailableTimes"
  if (eventData.totalTimeSlotCount === 0) return null

  const showPriceText = (
    (eventData.minPrice !== null) || (eventData.maxPrice !== null)) && (listingStatus === 'availableStandard'
  )

  const location = eventData.virtual ? intl.formatMessage(locale.virtualEvent) : eventData.address
  const showSocial = eventData.privacy === 'PUBLIC' || (eventData.privacy === 'PRIVATE' && eventData.socialButtons === true)

  return (
    <div className={style.container}>
      <div className={style.price}>
        <div>
          {showPriceText && intl.formatMessage(locale.price)}
        </div>
        <Price
          minPrice={eventData.minPrice}
          maxPrice={eventData.maxPrice}
          currency={eventData.transactionCurrency}
          listingStatus={listingStatus}
        />
      </div>

      <div className={style.content}>
        <div className={`${style.info} ${expanded ? '' : style.empty}`}>
          <div className={style.title}>{eventData.title}</div>
          {!eventData.hiddenDate && eventData.dateDisplayOption !== DATE_DISPLAY_OPTIONS.hideEventTime &&
            <div className={style.time}>
              <CalendarIcon className={style.time__icon} />
              {getEventTime()}
            </div>
          }
          <div className={style.location}>
            <MarkerIcon className={style.location__icon} />
            {location}
          </div>
        </div>
        <FanReminderInfo
          listingStatus={listingStatus}
          fanReminderEndStamp={eventData.fanReminderEndStamp}
          timezone={eventData.timezone}
        />
        <TicketButton
          listingStatus={listingStatus}
          ticketButtonText={ticketButtonText}
          isTimedEntry={isTimedEntry}
          eventId={eventData.id}
          baseUrl={baseUrl}
          hostId={eventData.user.id}
          viewer={viewer}
        />
        <Share />
      </div>
    </div>
  )
}

export const TicketWidget = injectIntl(TicketWidgetComponent)
